import React, { useEffect, useState } from 'react';
import PopupScreen from './PopupScreen';
import { TextField, Box, Typography } from '@mui/material';
import { getRandomMedImage } from './imageUtils';
import { PlayerSettings } from '../model/PlayerSettings';

interface StartScreenProps {
  open: boolean;
  onStart: (settings: PlayerSettings) => void;
  onCredits: () => void;
  isMobile: boolean; // Added prop for mobile detection
  isLandscape?: boolean; // Added prop for landscape detection
}

const StartScreen: React.FC<StartScreenProps> = ({ 
  open, 
  onStart, 
  onCredits, 
  isMobile,
  isLandscape = false // Default to false if not provided 
}) => {
  const [isReady, setIsReady] = useState<boolean>(false);
  const [randomImage, setRandomImage] = useState<string>("");
  const [playerName, setPlayerName] = useState<string>(
    localStorage.getItem('captainsChess.playerName') || ''
  );

  // Preload the random image before showing the component
  useEffect(() => {
    if (open) {
      // Reset ready state when opening
      setIsReady(false);
      
      // Get random image
      const image = getRandomMedImage();
      setRandomImage(image);
      
      // Preload the image
      const imageObj = new Image();
      imageObj.onload = () => {
        setIsReady(true); // Mark component as ready once image is loaded
      };
      imageObj.src = image;
    }
  }, [open]);

  const handleStart = () => {
    // Save player name to localStorage
    if (playerName.trim()) {
      localStorage.setItem('captainsChess.playerName', playerName.trim());
    }
    
    // Get other settings from localStorage
    const settings: PlayerSettings = {
      playerName: playerName.trim() || 'Player',
      useVintageBoard: localStorage.getItem('captainsChess.useVintageBoard') === 'true' || false,
      highlightMoves: localStorage.getItem('captainsChess.highlightMoves') !== 'false' // Default to true
    };
    
    onStart(settings);
  };

  // Define buttons for the start screen
  const startScreenButtons = [
    {
      text: "Start Game",
      onClick: handleStart,
      color: 'primary' as const,
      variant: 'contained' as const,
      delay: 8000,
      playAudio: true
    },
    {
      text: "About",
      onClick: onCredits,
      color: 'secondary' as const,
      variant: 'contained' as const
    }
  ];

  return (
    <PopupScreen
      open={open && isReady} // Only show when ready
      title="Captain's Chess"
      imageSrc={randomImage}
      audioSrc="/sounds/ocean1.wav"
      buttons={startScreenButtons}
      isMobile={isMobile} // Pass isMobile to PopupScreen
      isLandscape={isLandscape} // Pass isLandscape to PopupScreen
    >
      <Box sx={{ mt: 3, width: '100%', maxWidth: 400, mx: 'auto' }}>
        <Typography variant="body1" sx={{ mb: 2, color: 'white', textAlign: 'center' }}>
          Enter your chess name to begin
        </Typography>
        
        <TextField
          fullWidth
          label="Your Chess Name"
          variant="outlined"
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          margin="normal"
          inputProps={{ maxLength: 20 }}
          sx={{ 
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(255, 215, 0, 0.5)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 215, 0, 0.8)',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.8)',
            },
            '& .MuiOutlinedInput-input': {
              color: 'white',
            }
          }}
        />
      </Box>
    </PopupScreen>
  );
};

export default StartScreen;
